import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Text } from 'rsuite';
import HTLoader from '../assets/HT-loader.gif';
import { useExchangeCodeForTokenMutation } from './apaleoSlice';
import { useAuth } from '../contexts/AuthContext';
import './OAuthCallback.scss';

const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [exchangeToken] = useExchangeCodeForTokenMutation();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('Processing authorization...');
  const hasRun = useRef(false);

  useEffect(() => {
    const handleAuth = async () => {
      if (hasRun.current) return;
      hasRun.current = true;

      try {
        // Check for OAuth errors
        const oauthError = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (oauthError) {
          console.error('OAuth error:', oauthError, errorDescription);
          setError(errorDescription || 'Authorization was denied or failed.');
          setTimeout(() => navigate('/', { replace: true }), 3000);
          return;
        }

        // Get authorization code and state
        const code = searchParams.get('code');
        const state = searchParams.get('state');

        if (!code || !state) {
          setError('Missing authorization code or state parameter.');
          setTimeout(() => navigate('/', { replace: true }), 3000);
          return;
        }

        setStatus('Exchanging authorization code for access token...');
        console.log('Exchanging code for token...');

        const result = await exchangeToken({ code, state });

        if ('error' in result) {
          const errorMessage =
            (result.error as any)?.data?.message ||
            (result.error as any)?.message ||
            'Failed to exchange authorization code';
          throw new Error(errorMessage);
        }

        const { accessToken, refresh_token } = result.data;

        if (!accessToken) {
          throw new Error('Failed to retrieve access token');
        }

        setStatus('Authentication successful! Redirecting...');
        login({
          accessToken,
          refreshToken: refresh_token,
        });

        navigate('/property-selection', { replace: true });
      } catch (err: any) {
        console.error('Token exchange failed:', err);

        let errorMessage = 'Authentication failed. Please try again.';

        if (err?.message) {
          errorMessage = err.message;
        } else if (err?.data?.message) {
          errorMessage = err.data.message;
        } else if (typeof err === 'string') {
          errorMessage = err;
        }

        setError(errorMessage);
        setTimeout(() => navigate('/', { replace: true }), 3000);
      }
    };

    handleAuth();
  }, [searchParams, navigate, login, exchangeToken]);

  return (
    <div className="loader-backdrop" data-testid="loader">
      <div className="loader">
        <img src={HTLoader} alt="Loading..." />
      </div>
      {error && (
        <div>
          <h2>Authentication Failed</h2>
          <Text size="lg" className="error-text">
            {' '}
            Redirecting you back to the home page...
          </Text>
        </div>
      )}
      {!error && status && <Text size="lg">{status}</Text>}
      {!error && !status && <Text size="lg">Loading, please wait...</Text>}
    </div>
  );
};

export default OAuthCallback;
